import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { getAllergiesSelector, getHealthIssuesSelector } from '../../../core/selectors';
import { IPet, ReferenceOption } from '../../../core/types';
import { HealthChangesSummary } from '../components/HealthChangesSummary';
import { HealthPreviewRecipeSelector } from '../components/HealthPreviewRecipeSelector';
import { PriceDisplay } from '../components/PriceDisplay';
import { usePetHealthPreview } from '../hooks/usePetHealthPreview';
import { ArrowLeftIcon, CheckmarkIcon, LoadingSpinner, PackageIcon } from '../icons';

interface PetHealthPreviewStepProps {
  pet: IPet;
  selectedAllergies: string[];
  selectedHealthIssues: string[];
  recalculatedProduct: any;
  currentRecipes: string[];
  onBack: () => void;
  onSave: (variantId?: number) => void;
  isUpdating: boolean;
}

/**
 * The selections are German names; the reference options carry the label for
 * each. A linear lookup over at most 19 options reads more clearly than a
 * memoised map and runs once per render of a modal step.
 */
const labelsFor = (options: ReferenceOption[], names: string[]): string[] =>
  names.map((name) => options.find((option) => option.name === name)?.label ?? name);

export const PetHealthPreviewStep: React.FC<PetHealthPreviewStepProps> = (props) => {
  const { onBack, isUpdating, pet, selectedAllergies } = props;
  const allergyOptions = useSelector(getAllergiesSelector);
  const healthIssueOptions = useSelector(getHealthIssuesSelector);

  const {
    selectedRecipes,
    disabledRecipes,
    sachets,
    oldPrice,
    newPrice,
    currencyCode,
    discount,
    currentVariant,
    handleRecipeToggle,
    handleSaveClick,
  } = usePetHealthPreview(props);

  const allRecipesDisabled =
    selectedRecipes.length === 0 || selectedRecipes.every((r) => disabledRecipes.includes(r));

  return (
    <>
      <div className="product-preview-step">
        <div className="product-preview-step__header">
          <h3>
            <FormattedMessage id="PET_PROFILE.HEALTH_PREVIEW.TITLE" />
          </h3>
          <p>
            <FormattedMessage id="PET_PROFILE.HEALTH_PREVIEW.SUBTITLE" />
          </p>
        </div>

        <div className="product-preview-step__container">
          <div className="product-preview-step__main">
            <HealthChangesSummary
              oldAllergies={pet.allergies}
              newAllergies={selectedAllergies}
              oldHealthIssues={pet.health_issues}
              newHealthIssues={props.selectedHealthIssues}
              oldAllergyLabels={pet.allergy_labels ?? pet.allergies}
              newAllergyLabels={labelsFor(allergyOptions, selectedAllergies)}
              oldHealthIssueLabels={pet.health_issue_labels ?? pet.health_issues}
              newHealthIssueLabels={labelsFor(healthIssueOptions, props.selectedHealthIssues)}
              oldPrice={oldPrice}
              newPrice={newPrice}
              currencyCode={currencyCode}
              discount={discount}
            />
          </div>

          <div className="product-preview-step__sidebar">
            <div className="product-preview-step__section">
              <label className="product-preview-step__label">
                <PackageIcon />
                <FormattedMessage id="PET_PROFILE.HEALTH_PREVIEW.RECIPE_IMPACT" />
              </label>
              <HealthPreviewRecipeSelector
                selectedRecipes={selectedRecipes}
                disabledRecipes={disabledRecipes}
                petName={pet.name}
                sachets={sachets}
                onToggle={handleRecipeToggle}
              />
            </div>

            <PriceDisplay newPrice={newPrice} currencyCode={currencyCode} discount={discount} />
          </div>
        </div>

        <div className="product-preview-step__actions">
          <button
            type="button"
            onClick={onBack}
            className="ppu-action-button ppu-action-button--secondary">
            <ArrowLeftIcon />
            <FormattedMessage id="GLOBAL.BACK" />
          </button>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={isUpdating || !currentVariant || allRecipesDisabled}
            className={`ppu-action-button ppu-action-button--primary ${
              isUpdating ? 'ppu-action-button--loading' : ''
            }${allRecipesDisabled ? ' ppu-action-button--disabled' : ''}`}>
            {isUpdating ? (
              <>
                <LoadingSpinner />
                <FormattedMessage id="GLOBAL.SAVING" />
              </>
            ) : (
              <>
                <FormattedMessage id="GLOBAL.SAVE" />
                <CheckmarkIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};
